/* eslint-disable no-alert */
/* eslint-disable no-restricted-globals */
import PropTypes from 'prop-types';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { cloneDeep, findLastIndex, isNil } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useObservableState } from 'observable-hooks';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useProfile } from 'assets/js/profile';
import { AlarmLinesGraph, createEmptyAlarmPoints } from '../measuringPoint/AlarmLinesGraph';
import getDeleteMessage from '../../../utils/messages';
import {
    distanceConverter,
    revertDistanceConverter,
    roundConvertedMinValueForDisplay,
    roundToDistanceUnitDigits,
} from '../../../utils/formatting';
import {
    applyNamingConventionsByGuideline,
    defaultGuideline,
} from '../../../utils/naming-conventions';
import Tooltip from '../../../utils/tooltip';
import { BasePage } from '../BasePage';
import Button from '../../form/Button';
import SelectCombobox from '../../form/SelectCombobox';
import { useConfirm } from '../../confirm/Confirm';

import { schema } from '../../../schema';
import { getDataType } from '../../../graph/data-types';
import { DATA_TYPE } from '../../../graph/enums';
import { SwarmType } from '../../../enums';
import { useTranslatedFormSchema } from '../measuringPoint/schema-utils';

const CUSTOM_ALARM_FRAGMENT = gql`
    fragment CustomAlarmTypeFields on CustomAlarmType {
        id
        name
        points {
            freq
            vkar
        }
    }
`;

const GET_CUSTOM_ALARMS = gql`
    query GetCustomAlarms {
        customAlarms {
            edges {
                node {
                    ...CustomAlarmTypeFields
                }
            }
        }
    }
    ${CUSTOM_ALARM_FRAGMENT}
`;

const GET_CUSTOM_ALARM = gql`
    query GetCustomAlarm($id: ID!) {
        customAlarm(id: $id) {
            ...CustomAlarmTypeFields
        }
    }
    ${CUSTOM_ALARM_FRAGMENT}
`;

const CREATE_CUSTOM_ALARM = gql`
    mutation customAlarmCreate($input: CustomAlarmCreateMutationInput!) {
        customAlarmCreate(input: $input) {
            customAlarm {
                ...CustomAlarmTypeFields
            }
            errors {
                field
                message
            }
        }
    }
    ${CUSTOM_ALARM_FRAGMENT}
`;

const UPDATE_CUSTOM_ALARM = gql`
    mutation customAlarmUpdate($input: CustomAlarmUpdateMutationInput!) {
        customAlarmUpdate(input: $input) {
            customAlarm {
                ...CustomAlarmTypeFields
            }
            errors {
                field
                message
            }
        }
    }
    ${CUSTOM_ALARM_FRAGMENT}
`;

const DELETE_CUSTOM_ALARM = gql`
    mutation customAlarmDelete($input: CustomAlarmDeleteMutationInput!) {
        customAlarmDelete(input: $input) {
            customAlarm {
                id
            }
            errors {
                field
                message
            }
        }
    }
`;

// This is called 'legacy' because it still uses `main-container` instead of
// `main-container-new`. `main-container` ensures that this page can use some
// of Django's and PureCSS's form styling, along with some extra custom styles
// from honeycomb/static/css/main.scss. Ideally, this page should be styled with
// Tailwind so we can completely switch to `main-container-new` and remove
// `main-container` from the repo entirely.
function LegacyPage({ children, ...props }) {
    return (
        <BasePage {...props} className="main-container">
            {children}
        </BasePage>
    );
}
LegacyPage.propTypes = {
    children: PropTypes.node.isRequired,
};

function ListView() {
    const confirm = useConfirm();
    const { t } = useTranslation();

    const { loading, error, data } = useQuery(GET_CUSTOM_ALARMS, {
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-first',
    });

    const [deleteAlarm] = useMutation(DELETE_CUSTOM_ALARM, {
        update(cache, { data: { customAlarmDelete } }) {
            cache.modify({
                fields: {
                    // eslint-disable-next-line default-param-last
                    customAlarms(existingCustomAlarms, { readField }) {
                        return {
                            ...(existingCustomAlarms ?? {}),
                            edges: existingCustomAlarms?.edges.filter(
                                (customAlarm) =>
                                    customAlarmDelete.customAlarm.id !==
                                    readField('id', customAlarm.node)
                            ),
                        };
                    },
                },
            });
        },
    });

    function handleRemoveAlarm(id) {
        deleteAlarm({
            variables: { input: { id } },
        });
    }

    const nodes = useMemo(() => data?.customAlarms.edges.map((edge) => edge.node), [data]);

    return (
        <LegacyPage
            title={t('CUSTOM_ALARM_LEVEL')}
            besideTitle={
                <Button to="/alarm_level/create">{gettext('CUSTOM_ALARM_LEVEL_CREATE')}</Button>
            }
        >
            <div>
                {loading && <p>{gettext('LOADING')}</p>}
                {error && <p>{gettext('ERROR')}</p>}
                {!data?.customAlarms.edges.length &&
                    interpolate(gettext('CUSTOM_ALARM_LEVEL_NONE_CREATED'), [
                        gettext('CUSTOM_ALARM_LEVEL_CREATE'),
                    ])}
            </div>
            {!loading && !error && nodes.length ? (
                <div className="custom-alarms">
                    <table className="table-responsive-label table">
                        <thead>
                            <tr>
                                <th>{gettext('NAME')}</th>
                                <th>{gettext('ACTIONS')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {nodes.map((node) => (
                                <tr key={node.id}>
                                    <td data-label={gettext('NAME')} className="alarm_level-name">
                                        {node.name}
                                    </td>
                                    <td
                                        data-label={gettext('ACTIONS')}
                                        className="alarm-level-options"
                                    >
                                        <Link
                                            to={`/alarm_level/${node.id}/`}
                                            title={gettext('EDIT')}
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </Link>
                                        <button
                                            type="button"
                                            title={gettext('DELETE')}
                                            onClick={() =>
                                                confirm({
                                                    markdownMessage: getDeleteMessage(
                                                        t('CUSTOM_ALARM_LEVEL').toLowerCase(),
                                                        node.name
                                                    ),
                                                    onConfirm() {
                                                        handleRemoveAlarm(node.id);
                                                    },
                                                })
                                            }
                                        >
                                            <FontAwesomeIcon icon={faTrash} className="text-lg" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </LegacyPage>
    );
}

function cleanupPoints(points) {
    // Find the index of the last filled point.
    const index = findLastIndex(points, (point) => point.freq || point.vkar);

    // Remove all points after the last filled point, but
    // leave one to be filled in.
    const result = points.slice(0, index + 2);

    // Validation.
    let lastPoint = null;
    result.forEach((point, i) => {
        point.valid =
            // Valid if first:
            i === 0 ||
            // Valid if this is the new to be filled in row and not the last row:
            (i === result.length - 1 && i !== points.length - 1) ||
            // Valid if freq is equal or bigger than the last freq:
            lastPoint?.freq <= point.freq;

        lastPoint = point;
    });

    return result;
}

export function useApplyNamingConventions() {
    const [guideLine, setGuideLineState] = useState(defaultGuideline);

    const setGuideLine = useCallback(
        (value) => {
            // Run this here so that it happens before the re-render.
            applyNamingConventionsByGuideline(value);

            // Trigger re-render.
            setGuideLineState(value);
        },
        [setGuideLineState]
    );

    // Only run once to set the inital guide line.
    useEffect(() => {
        applyNamingConventionsByGuideline(defaultGuideline);
    }, [setGuideLine]);

    return [guideLine, setGuideLine];
}

function AddAlarmUpdateView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const profile = useProfile();
    const isAddMode = !id;

    const [vtopTitle] = useObservableState(() =>
        getDataType(DATA_TYPE.VTOP).getAxisTitle$({ profile })
    );
    const [fdomTitle] = useObservableState(() =>
        getDataType(DATA_TYPE.FDOM).getAxisTitle$({ profile })
    );

    const {
        loading: getCustomAlarmLoading,
        error: getCustomAlarmError,
        data: getCustomAlarmData,
    } = useQuery(GET_CUSTOM_ALARM, {
        variables: { id },
        skip: isAddMode,
    });

    const [updateAlarm] = useMutation(UPDATE_CUSTOM_ALARM);

    const [addAlarm, { loading: mutationLoading, error: mutationError }] = useMutation(
        CREATE_CUSTOM_ALARM,
        {
            update(
                cache,
                {
                    data: {
                        customAlarmCreate: { customAlarm },
                    },
                }
            ) {
                if (!customAlarm) {
                    return;
                }

                cache.modify({
                    broadcast: false,
                    fields: {
                        customAlarms(existingCustomAlarms) {
                            return {
                                ...existingCustomAlarms,
                                edges: [
                                    ...existingCustomAlarms.edges,
                                    {
                                        node: customAlarm,
                                    },
                                ],
                            };
                        },
                    },
                });
            },
        }
    );

    const [alarm, setState] = useState({
        name: '',
        points: createEmptyAlarmPoints(),
    });

    const formSchema = useTranslatedFormSchema(SwarmType.Vibration, false);
    const flatLevelField = formSchema.fields.getByName('flatLevel');
    const translatedGuideLineOptions = useMemo(
        () => new Map(formSchema.fields.getByName('guideLine').options),
        [formSchema.fields]
    );

    const guideLineOptions = useMemo(
        () =>
            Array.from(schema.customAlarmLevelPreviewGuidelines).map((key) => ({
                value: key,
                label: translatedGuideLineOptions.get(key) ?? key,
            })),
        [translatedGuideLineOptions]
    );

    const [guideLine, setGuideLine] = useApplyNamingConventions();

    const [notExisting, setNotExisting] = useState(false);

    function handleNameChange(event) {
        setState((old) => ({ ...old, name: event.target.value }));
    }

    function handlefreqVkarChange(type, event) {
        const dataKey = event.target.getAttribute('data-key');

        setState((previousState) => {
            const state = { ...previousState };
            const { value } = event.target;
            if (value === '') {
                state.points[dataKey][type] = null;
                return state;
            }

            const parsedValue = parseFloat(value);
            state.points[dataKey][type] = parsedValue;

            return state;
        });
    }

    function handleFreqChange(event) {
        return handlefreqVkarChange('freq', event);
    }

    function handleVkarChange(event) {
        return handlefreqVkarChange('vkar', event);
    }

    function setVkarValidationMessage(event) {
        const { value, validity } = event.target;

        event.target.setCustomValidity('');

        if (value === '') {
            return;
        }

        if (validity.rangeUnderflow) {
            event.target.setCustomValidity(
                interpolate(
                    gettext('MIN_VALUE_VALIDATION'),
                    {
                        minValue: roundConvertedMinValueForDisplay(
                            flatLevelField,
                            flatLevelField.minValue,
                            profile.distanceUnit
                        ),
                    },
                    true
                )
            );
        }
    }

    async function onSubmit(event) {
        event.preventDefault();

        const mutationOptions = {
            variables: {
                input: {
                    name: alarm.name,
                    points: alarm.points.map(({ freq, vkar }) => ({
                        freq,
                        vkar: vkar && revertDistanceConverter(vkar, profile.distanceUnit),
                    })),
                },
            },
        };

        if (!isAddMode) {
            // Add ID for the update.
            mutationOptions.variables.input.id = id;

            updateAlarm(mutationOptions);
        } else {
            addAlarm(mutationOptions);
        }

        navigate('/alarm_level/');
    }

    useEffect(() => {
        if (getCustomAlarmData) {
            if (!getCustomAlarmData.customAlarm) {
                setNotExisting(true);
                return;
            }

            const customAlarm = cloneDeep(getCustomAlarmData.customAlarm);

            // Do distance converting.
            customAlarm.points = customAlarm.points.map(({ freq, vkar }) => ({
                freq,
                vkar:
                    vkar &&
                    roundToDistanceUnitDigits(
                        distanceConverter(vkar, profile.distanceUnit),
                        'flatLevel',
                        profile.distanceUnit
                    ),
            }));

            setState(customAlarm);
        }
    }, [getCustomAlarmData, profile.distanceUnit]);

    if (getCustomAlarmLoading) return <p>{gettext('LOADING')}</p>;
    if (getCustomAlarmError || notExisting) return <p>{gettext('ERROR')}</p>;

    const points = cleanupPoints(alarm.points);

    return (
        <LegacyPage title={t('CUSTOM_ALARM_LEVEL')}>
            <div className="flex">
                <div className="w-1/2">
                    <form className="form custom-alarm-level-form" onSubmit={onSubmit}>
                        <li>
                            <label htmlFor="name">{gettext('NAME')}:</label>
                            <input
                                type="text"
                                id="name"
                                value={alarm.name}
                                onChange={handleNameChange}
                                required
                            />
                        </li>
                        <li>
                            <table id="alarm_settings">
                                <thead>
                                    <tr>
                                        <th>{fdomTitle}</th>
                                        <th>{vtopTitle}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {points.map((point, i) => {
                                        // Fields are required if they are on the first
                                        // row or not on the last row.
                                        const required = i === 0 || i !== points.length - 1;

                                        return (
                                            <tr key={i} className="field_{{ field }}">
                                                <td>
                                                    <input
                                                        type="number"
                                                        data-key={i}
                                                        title={`freq ${i}`}
                                                        value={point.freq ?? ''}
                                                        onChange={handleFreqChange}
                                                        required={required}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        data-key={i}
                                                        title={`vkar ${i}`}
                                                        value={isNil(point.vkar) ? '' : point.vkar}
                                                        onChange={handleVkarChange}
                                                        onInput={setVkarValidationMessage}
                                                        onInvalid={setVkarValidationMessage}
                                                        min={flatLevelField.minValue}
                                                        step="any"
                                                        required={required}
                                                    />
                                                </td>
                                                <td>
                                                    {!point.valid && (
                                                        <Tooltip
                                                            content={gettext(
                                                                'CUSTOM_ALARM_LEVEL_POINT_INVALID'
                                                            )}
                                                        >
                                                            <i className="fa fa-exclamation-circle"></i>
                                                        </Tooltip>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </li>
                        <div className="form-actions">
                            <button
                                className="pure-button pure-button-primary"
                                type="submit"
                                disabled={points.some((p) => !p.valid)}
                            >
                                {gettext('SAVE')}
                            </button>

                            <Link to="/alarm_level/" className="pure-button">
                                {gettext('CANCEL')}
                            </Link>
                        </div>
                        {mutationLoading && <p>{gettext('LOADING')}</p>}
                        {mutationError && <p>{gettext('ERROR')}</p>}
                    </form>
                </div>

                <div className="w-1/2">
                    <div className="guide-line-select">
                        <label>{gettext('Guideline')}: </label>
                        <SelectCombobox
                            options={guideLineOptions}
                            value={guideLine}
                            onChange={setGuideLine}
                        />
                    </div>
                    <AlarmLinesGraph alarms={[alarm]} />
                </div>
            </div>
        </LegacyPage>
    );
}

export function CustomAlarmPageRoutes() {
    return (
        <Routes>
            <Route index element={<ListView />} />
            <Route path=":id" element={<AddAlarmUpdateView />} />
            <Route path="create" element={<AddAlarmUpdateView />} />
        </Routes>
    );
}
